import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, SYNC_SUFFIX } from "@obg/ngrx-utils";

import { AppError } from "../../app-error/app-error.model";

import {
	ExperianData,
	ExperianDataRequest,
	NemIdAuthInitializeSuccessPayload,
	NemIdVerificationStatusResponse,
	OpenIdConnectAuthRequest,
	OpenIdConnectUrlRequest,
} from "./dga-auth-transaction.model";

export const ACTION_PREFIX = "[DGA Auth Transaction]";
export const DGA_AUTH_TRANSACTION_ACTION_TYPE = {
	/**
	 * Experian data
	 */
	getExperianData: `${ACTION_PREFIX} Get Experian Data`,
	getExperianDataSuccess: `${ACTION_PREFIX} Get Experian Data Success`,
	getExperianDataFail: `${ACTION_PREFIX} Get Experian Data Fail`,
	setExperianData: `${ACTION_PREFIX} Set Experian Data ${SYNC_SUFFIX}`,
	/**
	 * Starts auth transaction with NemID parameters
	 */
	initializeAuthTransaction: `${ACTION_PREFIX} Initialize Auth Transaction`,
	initializeAuthTransactionSuccess: `${ACTION_PREFIX} Initialize Auth Transaction Success`,
	initializeAuthTransactionFail: `${ACTION_PREFIX} Initialize Auth Transaction Fail`,
	/**
	 * Dispatched to retrieve verification status for a specific token.
	 */
	getVerificationStatus: `${ACTION_PREFIX} Get Verification Status`,
	getVerificationStatusSuccess: `${ACTION_PREFIX} Get Verification Status Success`,
	getVerificationStatusFail: `${ACTION_PREFIX} Get Verification Status Fail`,
	/**
	 * Sets DGA auth transaction values to undefined in the state.
	 */
	reset: `${ACTION_PREFIX} Reset ${SYNC_SUFFIX}`,
	resetVerificationStatus: `${ACTION_PREFIX} Reset Verification Status ${SYNC_SUFFIX}`,
	resetXmlDSig: `${ACTION_PREFIX} Reset XmlDSig ${SYNC_SUFFIX}`,
	/*
	 * Sets the current journey in the state.
	 */
	setCurrentJourney: `${ACTION_PREFIX} Set Current Journey ${SYNC_SUFFIX}`,
	/**
	 * Sets the loginToRegistrationFlow value to 'true' in the state.
	 */
	setLoginToRegistrationFlow: `${ACTION_PREFIX} Set Login To Registration Flow ${SYNC_SUFFIX}`,
	/**
	 * Dispatched to retrieve the Open ID Connect broker url from api and sets it in the state.
	 */
	setOpenIdConnectUrl: `${ACTION_PREFIX} Set Open Id Connect App Url`,
	setOpenIdConnectUrlSuccess: `${ACTION_PREFIX} Set Open Id Connect App Url Success`,
	setOpenIdConnectUrlFail: `${ACTION_PREFIX} Set Open Id Connect App Url Fail`,
	/**
	 * Sets the CPR number which is required for DGA auth in the state.
	 */
	setCpr: `${ACTION_PREFIX} Set cpr`,
	/**
	 * Initializes the DGA auth transaction through the Open ID Connect broker.
	 */
	initializeOidcAuthTransaction: `${ACTION_PREFIX} Initialize Oidc Auth Transaction`,
};

@Injectable({
	providedIn: "root",
})
export class DgaAuthTransactionAction {
	getVerificationStatus(payload: NemIdAuthInitializeSuccessPayload): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
			payload,
		};
	}

	getVerificationStatusSuccess(payload: NemIdVerificationStatusResponse): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess,
			payload,
		};
	}

	getVerificationStatusFail(error: AppError | Error): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail,
			payload: error,
		};
	}

	resetVerificationStatus(): Action {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.resetVerificationStatus,
		};
	}

	reset(): Action {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.reset,
		};
	}

	getExperianData(payload: ExperianDataRequest): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianData,
			payload,
		};
	}

	getExperianDataSuccess(payload: ExperianData): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianDataSuccess,
			payload,
		};
	}

	getExperianDataFail(error: AppError): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianDataFail,
			payload: error,
		};
	}

	setExperianData(payload: ExperianData): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.setExperianData,
			payload,
		};
	}

	setCpr(payload: string): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.setCpr,
			payload,
		};
	}

	setOpenIdConnectUrl(payload: OpenIdConnectUrlRequest): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrl,
			payload,
		};
	}

	setCurrentJourney(payload: string): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.setCurrentJourney,
			payload,
		};
	}

	setOpenIdConnectUrlSuccess(payload: string): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrlSuccess,
			payload,
		};
	}

	setOpenIdConnectUrlFail(error: AppError): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrlFail,
			payload: error,
		};
	}

	initializeOidcAuthTransaction(payload: OpenIdConnectAuthRequest): ActionWithPayload {
		return {
			type: DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeOidcAuthTransaction,
			payload,
		};
	}
}
